import { AgGrid, AgGridReact } from "@jsq/ds/core";
import { ControlTowerContext } from "#utils/context";
import { UNASSIGNED_FILTER_VALUE } from "../constants";
import {
  WorkTypeRenderer,
  JiraLinkRenderer,
  AssigneeRenderer,
  ProgressRenderer,
  ClientRenderer,
  TimeElapsedRenderer,
  StatusRenderer,
  PastDueRenderer,
  TextCellRenderer,
  EntityRenderer,
} from "./cellRenderers";
import { getClientCellClass, formatDateValue } from "./columnHelpers";
import * as styles from "./styles_vanilla.css";
import {
  isFilterOptionsWithMapping,
  type TicketRowItem,
  type FilterOptionsMap,
  type FilterOptionsWithMapping,
} from "./types";

const getFilterValuesForMapping = (option: FilterOptionsWithMapping | undefined): string[] => {
  return option?.displayValues ?? [];
};

const getFilterValuesForMappingWithUnassigned = (
  option: FilterOptionsWithMapping | undefined,
): string[] => {
  const values = option?.displayValues ?? [];
  return [UNASSIGNED_FILTER_VALUE, ...values];
};

const getFilterValuesForArray = (options: string[] | undefined): string[] => {
  return options ?? [];
};

const getFilterValuesForArrayWithUnassigned = (options: string[] | undefined): string[] => {
  const values = options ?? [];
  return [UNASSIGNED_FILTER_VALUE, ...values];
};

const getFilterValues = (
  filterOption: string[] | FilterOptionsWithMapping | undefined,
  includeUnassigned = false,
): string[] => {
  if (isFilterOptionsWithMapping(filterOption)) {
    return includeUnassigned
      ? getFilterValuesForMappingWithUnassigned(filterOption)
      : getFilterValuesForMapping(filterOption);
  }
  return includeUnassigned
    ? getFilterValuesForArrayWithUnassigned(filterOption)
    : getFilterValuesForArray(filterOption);
};

export const getColumnDefs = (
  filterOptions?: FilterOptionsMap,
  context?: ControlTowerContext,
  showGpControlTower?: boolean,
): AgGridReact.AgGridReactProps["columnDefs"] => {
  const isCanopy = context === ControlTowerContext.CANOPY;

  return [
    {
      field: "key",
      headerName: "Ticket ID",
      sortable: true,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals"],
        suppressAndOrCondition: true,
        defaultOption: "contains",
      },
      hide: false,
      width: 140,
      cellRenderer: isCanopy ? JiraLinkRenderer : TextCellRenderer,
      cellClass: styles.cellFlexCenter,
      checkboxSelection: true,
      headerCheckboxSelection: false,
    },
    {
      field: "description",
      headerName: "Description",
      sortable: false,
      filter: "agTextColumnFilter",
      filterParams: {
        debounceMs: 1000,
        filterOptions: ["contains", "equals"],
        suppressAndOrCondition: true,
        defaultOption: "contains",
      },
      hide: false,
      width: 250,
      cellClass: styles.cellDescription,
      tooltipField: "description",
    },
    {
      field: "workType",
      headerName: "Type",
      sortable: false,
      filter: "agSetColumnFilter",
      filterParams: {
        debounceMs: 300,
        buttons: ["reset"],
        values: getFilterValues(filterOptions?.workType),
      },
      hide: false,
      width: 140,
      cellRenderer: WorkTypeRenderer,
      cellClass: styles.cellFlexCenterWithEllipsis,
      tooltipField: "workType",
    },
    isCanopy && {
      field: "customers",
      headerName: "Client",
      sortable: false,
      filter: "agSetColumnFilter",
      filterParams: {
        debounceMs: 300,
        buttons: ["reset"],
        values: getFilterValues(filterOptions?.customers, true),
      },
      hide: false,
      width: 200,
      cellRenderer: ClientRenderer,
      cellClass: getClientCellClass,
      valueGetter: (params: AgGrid.ValueGetterParams<TicketRowItem>) => {
        const customers = params.data?.customers;
        if (!customers || customers.length === 0) return null;
        return customers[0];
      },
    },
    {
      field: "entityName",
      headerName: "Entity",
      sortable: true,
      filter: "agSetColumnFilter",
      filterParams: {
        debounceMs: 300,
        buttons: ["reset"],
        values: getFilterValues(filterOptions?.entityName, true),
      },
      hide: false,
      width: 180,
      cellRenderer: EntityRenderer,
      cellClass: styles.cellFlexCenter,
    },
    {
      field: "assignedTo",
      headerName: "Assigned to",
      sortable: true,
      filter: "agSetColumnFilter",
      filterParams: {
        debounceMs: 300,
        buttons: ["reset"],
        values: getFilterValues(filterOptions?.assignee, true),
      },
      valueGetter: (params: AgGrid.ValueGetterParams<TicketRowItem>) =>
        params.data?.assignedTo?.displayName ?? null,
      hide: false,
      width: 150,
      cellRenderer: AssigneeRenderer,
      cellClass: styles.cellFlexCenter,
    },
    (isCanopy || showGpControlTower) && {
      field: "requestedBy",
      headerName: "Requested by",
      sortable: true,
      filter: "agTextColumnFilter",
      filterParams: {
        debounceMs: 1000,
        filterOptions: ["contains"],
        suppressAndOrCondition: true,
        defaultOption: "contains",
      },
      hide: false,
      width: 150,
      cellRenderer: TextCellRenderer,
      cellClass: styles.cellFlexCenter,
    },
    isCanopy && {
      field: "faPreparer",
      headerName: "FA preparer",
      sortable: true,
      filter: "agSetColumnFilter",
      filterParams: {
        debounceMs: 300,
        buttons: ["reset"],
        values: getFilterValues(filterOptions?.faPreparer, true),
      },
      hide: false,
      width: 150,
      cellRenderer: TextCellRenderer,
      cellClass: styles.cellFlexCenter,
    },
    isCanopy && {
      field: "faReviewer",
      headerName: "FA reviewer",
      sortable: true,
      filter: "agSetColumnFilter",
      filterParams: {
        debounceMs: 300,
        buttons: ["reset"],
        values: getFilterValues(filterOptions?.faReviewer, true),
      },
      hide: false,
      width: 150,
      cellRenderer: TextCellRenderer,
      cellClass: styles.cellFlexCenter,
    },
    isCanopy && {
      field: "isPrimary",
      headerName: "IS Preparer",
      sortable: true,
      filter: "agSetColumnFilter",
      filterParams: {
        debounceMs: 300,
        buttons: ["reset"],
        values: getFilterValues(filterOptions?.isPrimary, true),
      },
      hide: false,
      width: 150,
      cellRenderer: TextCellRenderer,
      cellClass: styles.cellFlexCenter,
    },
    isCanopy && {
      field: "isReviewer",
      headerName: "IS reviewer",
      sortable: true,
      filter: "agSetColumnFilter",
      filterParams: {
        debounceMs: 300,
        buttons: ["reset"],
        values: getFilterValues(filterOptions?.isReviewer, true),
      },
      hide: false,
      width: 150,
      cellRenderer: TextCellRenderer,
      cellClass: styles.cellFlexCenter,
    },
    {
      field: "progress",
      headerName: "Progress",
      sortable: false,
      filter: false,
      hide: false,
      width: 120,
      cellRenderer: ProgressRenderer,
      cellClass: styles.cellFlexCenter,
    },
    isCanopy && {
      field: "status",
      headerName: "Status",
      sortable: true,
      filter: "agSetColumnFilter",
      filterParams: {
        debounceMs: 300,
        buttons: ["reset"],
        values: filterOptions?.status || [],
      },
      hide: false,
      width: 150,
      cellRenderer: StatusRenderer,
      cellClass: styles.cellFlexCenter,
    },
    (isCanopy || showGpControlTower) && {
      field: "statusCategoryName",
      headerName: isCanopy ? "Status category" : "Status",
      sortable: true,
      filter: "agSetColumnFilter",
      filterParams: {
        debounceMs: 300,
        buttons: ["reset"],
        values: ["Done", "To Do", "In Progress"],
      },
      hide: false,
      width: 150,
      cellRenderer: TextCellRenderer,
      cellClass: styles.cellFlexCenter,
    },
    {
      field: "dueDate",
      headerName: "Due date",
      sortable: true,
      filter: "agDateColumnFilter",
      filterParams: {
        debounceMs: 300,
        buttons: ["reset"],
        filterOptions: [
          "equals",
          "greaterThan",
          "greaterThanOrEqual",
          "lessThan",
          "lessThanOrEqual",
          "inRange",
        ],
        maxNumConditions: 1,
      },
      hide: false,
      width: 120,
      valueFormatter: formatDateValue,
      cellClass: styles.cellDate,
    },
    isCanopy && {
      field: "pastDue",
      headerName: "Past due",
      sortable: false,
      filter: false,
      hide: false,
      width: 100,
      cellRenderer: PastDueRenderer,
      cellClass: styles.cellFlexCenter,
    },
    (isCanopy || showGpControlTower) && {
      field: "updated",
      headerName: "Updated",
      sortable: true,
      filter: false,
      hide: false,
      width: 120,
      valueFormatter: formatDateValue,
      cellClass: styles.cellDate,
    },
    {
      field: "createdAt",
      headerName: isCanopy ? "Created at" : "Created",
      sortable: true,
      filter: false,
      hide: false,
      width: 120,
      sort: "desc" as const,
      valueFormatter: formatDateValue,
      cellClass: styles.cellDate,
    },
    isCanopy && {
      field: "timeElapsed",
      headerName: "Time elapsed",
      sortable: false,
      filter: false,
      hide: false,
      width: 120,
      cellRenderer: TimeElapsedRenderer,
      cellClass: styles.cellFlexCenter,
    },
  ].filter(Boolean) as AgGridReact.AgGridReactProps["columnDefs"];
};

export const columnDefs = getColumnDefs();
